import { MessageCircle, Minus, Paperclip, SendHorizontal } from 'lucide-react';
import { useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import { ToggleGroup, ToggleGroupItem } from '@nucleus-commerce/ui/components/toggle-group';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { readableOn } from '../lib/contrast';
import type { AppearanceForm } from '../schemas/appearance-schema';

export type PreviewStatus = 'online' | 'away' | 'offline';
type Device = 'desktop' | 'phone';

export interface WidgetPreviewProps {
  appearance: AppearanceForm['appearance'];
  logoUrl: string | null;
  attentionGrabberUrl: string | null;
  allowUploads?: boolean;
}

const STATUS_DOT: Record<PreviewStatus, string> = {
  online: 'bg-success',
  away: 'bg-warning',
  offline: 'bg-muted-foreground',
};

/**
 * The same colour rule as the widgets: the store's hex with black or white text, or the
 * storefront's primary tokens when none is set.
 */
function themeStyle(primaryColor: string): CSSProperties {
  const foreground = primaryColor ? readableOn(primaryColor) : null;
  return {
    '--chat-primary': foreground ? primaryColor : 'var(--primary)',
    '--chat-primary-foreground': foreground ?? 'var(--primary-foreground)',
  } as CSSProperties;
}

function Launcher({ shape }: { shape: 'round' | 'pill' }) {
  const { t } = useTranslation('chatSettings');
  const pill = shape === 'pill';
  return (
    <span
      data-testid="preview-launcher"
      data-shape={shape}
      className={cn(
        'inline-flex items-center justify-center gap-2 bg-(--chat-primary) text-(--chat-primary-foreground) shadow-lg',
        pill ? 'h-10 rounded-full px-4' : 'size-12 rounded-full',
      )}
    >
      <MessageCircle className="size-5" aria-hidden />
      {pill ? <span className="text-sm font-medium">{t('preview.launcherPill')}</span> : null}
    </span>
  );
}

function Panel({
  appearance,
  status,
  logoUrl,
  allowUploads,
  className,
}: {
  appearance: AppearanceForm['appearance'];
  status: PreviewStatus;
  logoUrl: string | null;
  allowUploads: boolean;
  className?: string;
}) {
  const { t } = useTranslation('chatSettings');
  const greeting =
    status === 'online'
      ? appearance.greetingOnline
      : status === 'away'
        ? appearance.greetingAway
        : appearance.greetingOffline;
  const title = appearance.headerTitle.trim() || t('preview.defaultTitle');
  return (
    <div
      data-testid="preview-panel"
      className={cn(
        'bg-background text-foreground flex flex-col overflow-hidden border shadow-2xl',
        className,
      )}
    >
      <div className="flex items-center gap-3 bg-(--chat-primary) px-3 py-2.5 text-(--chat-primary-foreground)">
        {logoUrl ? (
          <img src={logoUrl} alt="" className="bg-background size-8 rounded-full object-contain" />
        ) : null}
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium" data-testid="preview-title">
            {title}
          </p>
          <p className="flex items-center gap-1.5 text-xs opacity-90">
            <span className={cn('size-2 shrink-0 rounded-full', STATUS_DOT[status])} aria-hidden />
            <span className="truncate" data-testid="preview-greeting">
              {greeting}
            </span>
          </p>
        </div>
        <Minus className="size-4 opacity-80" aria-hidden />
      </div>
      {status === 'offline' ? (
        <div className="flex flex-1 flex-col gap-2 p-3 text-xs">
          <p className="text-muted-foreground">{t('preview.offlineIntro')}</p>
          <span className="bg-muted h-7 rounded-md" />
          <span className="bg-muted h-7 rounded-md" />
          <span className="bg-muted h-14 rounded-md" />
          <span className="mt-auto inline-flex h-8 items-center justify-center rounded-md bg-(--chat-primary) text-(--chat-primary-foreground)">
            {t('preview.offlineSend')}
          </span>
        </div>
      ) : (
        <>
          <div className="flex flex-1 flex-col gap-2 p-3 text-xs">
            <div className="flex items-end gap-2">
              {appearance.showAgentAvatars ? (
                <span
                  data-testid="preview-agent-avatar"
                  className="bg-muted text-muted-foreground flex size-6 shrink-0 items-center justify-center rounded-full text-[10px] font-medium"
                  aria-hidden
                >
                  {t('preview.agentInitial')}
                </span>
              ) : null}
              <p className="bg-muted max-w-[80%] rounded-lg rounded-bl-sm px-2.5 py-1.5">
                {t('preview.agentMessage')}
              </p>
            </div>
            <p className="ml-auto max-w-[80%] rounded-lg rounded-br-sm bg-(--chat-primary) px-2.5 py-1.5 text-(--chat-primary-foreground)">
              {t('preview.visitorMessage')}
            </p>
          </div>
          <div className="flex items-center gap-2 border-t px-3 py-2">
            {allowUploads ? (
              <Paperclip className="text-muted-foreground size-4" aria-hidden />
            ) : null}
            <span className="text-muted-foreground flex-1 truncate text-xs">
              {t('preview.composer')}
            </span>
            <SendHorizontal className="size-4 text-(--chat-primary)" aria-hidden />
          </div>
        </>
      )}
    </div>
  );
}

/**
 * A static picture of the storefront widget for the Appearance tab: launcher, panel, colour,
 * side and greeting per status, on a desktop page or a phone. Not the widget itself (apps cannot
 * share components); it mirrors the reference widget's layout and the shared colour rule.
 */
export function WidgetPreview({
  appearance,
  logoUrl,
  attentionGrabberUrl,
  allowUploads = true,
}: WidgetPreviewProps) {
  const { t } = useTranslation('chatSettings');
  const [status, setStatus] = useState<PreviewStatus>('online');
  const [device, setDevice] = useState<Device>('desktop');
  const [open, setOpen] = useState(true);
  const side = device === 'phone' ? appearance.mobilePosition : appearance.position;
  const sideClass = side === 'left' ? 'left-3' : 'right-3';

  return (
    <div className="flex flex-col gap-3" style={themeStyle(appearance.primaryColor)}>
      <div className="flex flex-wrap items-center gap-2">
        <ToggleGroup
          type="single"
          size="sm"
          variant="outline"
          value={status}
          onValueChange={(value) => {
            if (value) setStatus(value as PreviewStatus);
          }}
          aria-label={t('preview.statusLabel')}
        >
          {(['online', 'away', 'offline'] as const).map((value) => (
            <ToggleGroupItem key={value} value={value}>
              {t(`preview.status.${value}`)}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
        <ToggleGroup
          type="single"
          size="sm"
          variant="outline"
          value={device}
          onValueChange={(value) => {
            if (value) setDevice(value as Device);
          }}
          aria-label={t('preview.deviceLabel')}
        >
          <ToggleGroupItem value="desktop">{t('preview.device.desktop')}</ToggleGroupItem>
          <ToggleGroupItem value="phone">{t('preview.device.phone')}</ToggleGroupItem>
        </ToggleGroup>
        <ToggleGroup
          type="single"
          size="sm"
          variant="outline"
          value={open ? 'open' : 'closed'}
          onValueChange={(value) => {
            if (value) setOpen(value === 'open');
          }}
          aria-label={t('preview.openLabel')}
        >
          <ToggleGroupItem value="open">{t('preview.open')}</ToggleGroupItem>
          <ToggleGroupItem value="closed">{t('preview.closed')}</ToggleGroupItem>
        </ToggleGroup>
      </div>
      <div
        role="img"
        aria-label={t('preview.label', {
          side: t(`fields.side.${side}`),
          shape: t(`fields.shape.${appearance.shape}`),
        })}
        data-testid="widget-preview"
        data-side={side}
        className={cn(
          'bg-muted/40 relative mx-auto w-full overflow-hidden rounded-lg border',
          device === 'phone' ? 'h-[30rem] max-w-[17rem] rounded-[1.5rem] border-4' : 'h-[30rem]',
        )}
      >
        <div className="space-y-2 p-4" aria-hidden>
          <span className="bg-muted block h-3 w-1/3 rounded" />
          <span className="bg-muted block h-24 rounded" />
          <span className="bg-muted block h-3 w-2/3 rounded" />
          <span className="bg-muted block h-3 w-1/2 rounded" />
        </div>
        {open && device === 'phone' ? (
          <Panel
            appearance={appearance}
            status={status}
            logoUrl={logoUrl}
            allowUploads={allowUploads}
            className="absolute inset-0 border-0"
          />
        ) : null}
        {open && device === 'desktop' ? (
          <Panel
            appearance={appearance}
            status={status}
            logoUrl={logoUrl}
            allowUploads={allowUploads}
            className={cn('absolute bottom-18 h-[22rem] w-[17rem] rounded-xl', sideClass)}
          />
        ) : null}
        {open && device === 'phone' ? null : (
          <div className={cn('absolute bottom-3 flex flex-col items-end gap-2', sideClass)}>
            {attentionGrabberUrl && !open ? (
              <img
                src={attentionGrabberUrl}
                alt=""
                data-testid="preview-attention-grabber"
                className="max-h-24 max-w-32 object-contain"
              />
            ) : null}
            <Launcher shape={appearance.shape} />
          </div>
        )}
      </div>
    </div>
  );
}
